/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { CozeBrand } from '@coze-studio/components/coze-brand';
import { SignFrame, SignPanel } from '@coze-arch/bot-semi';

import { IconEye, IconEyeOff, IconLock, IconMail } from './icons';

import { useLoginService } from './service';
import { Favicon } from './favicon';
import { ReactComponent as GoogleIcon } from '../../../../../../apps/coze-studio/assets/google.svg';

/* ── inline styles (mirrors OmniChat LoginForm.vue approach) ── */

const s = {
  field: {
    display: 'grid',
    gap: 5,
  } as React.CSSProperties,

  label: {
    fontSize: 12,
    fontWeight: 600,
    color: '#374151',
  } as React.CSSProperties,

  inputWrap: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
  } as React.CSSProperties,

  inputIcon: {
    position: 'absolute' as const,
    left: 11,
    color: '#9ca3af',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none' as const,
  } as React.CSSProperties,

  input: {
    width: '100%',
    minHeight: 42,
    padding: '9px 12px 9px 36px',
    border: '1.5px solid #e2e8f0',
    borderRadius: 10,
    fontSize: 13,
    color: '#162033',
    background: '#fff',
    boxSizing: 'border-box' as const,
    outline: 'none',
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
  } as React.CSSProperties,

  submitBtn: {
    width: '100%',
    minHeight: 42,
    border: 0,
    borderRadius: 10,
    padding: '10px 14px',
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    background: '#F0B429',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(240,180,41,0.35)',
    transition: 'opacity 150ms ease, transform 150ms ease',
  } as React.CSSProperties,

  submitBtnDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
    transform: 'none',
    boxShadow: 'none',
  } as React.CSSProperties,

  divider: {
    position: 'relative' as const,
    display: 'grid',
    placeItems: 'center',
    margin: '4px 0',
  } as React.CSSProperties,

  dividerLine: {
    content: '""',
    position: 'absolute' as const,
    inset: '50% 0 auto',
    borderTop: '1px solid #e5e7eb',
    width: '100%',
  } as React.CSSProperties,

  dividerSpan: {
    position: 'relative' as const,
    padding: '0 10px',
    background: '#fff',
    color: '#9ca3af',
    fontSize: 12,
    fontWeight: 500,
  } as React.CSSProperties,

  oauthBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    width: '100%',
    minHeight: 42,
    border: '1.5px solid #e2e8f0',
    borderRadius: 10,
    padding: '10px 14px',
    background: '#fff',
    color: '#374151',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    boxSizing: 'border-box' as const,
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
  } as React.CSSProperties,

  switchCopy: {
    margin: 0,
    textAlign: 'center' as const,
    fontSize: 12,
    color: '#6b7280',
  } as React.CSSProperties,

  inlineLink: {
    border: 0,
    padding: 0,
    background: 'transparent',
    color: '#F0B429',
    fontSize: 'inherit',
    fontWeight: 600,
    cursor: 'pointer',
  } as React.CSSProperties,
};

/* ── Component ── */

export const LoginPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [focusedField, setFocusedField] = useState<'email' | 'password' | null>(null);

  const { login, loginWithGoogle, loginLoading, googleLoading } = useLoginService({ email, password });
  const redirect = searchParams.get('redirect')?.trim();
  const registerPath = redirect
    ? `/sign/register?${new URLSearchParams({ redirect }).toString()}`
    : '/sign/register';
  // Baked in at build time (rsbuild define) from the same env the backend uses
  const registrationDisabled =
    process.env.DISABLE_USER_REGISTRATION?.trim() === 'true';

  const loginDisabled = !email || !password || loginLoading || googleLoading;

  const inputStyle = (field: 'email' | 'password') => ({
    ...s.input,
    ...(focusedField === field
      ? { borderColor: '#F0B429', boxShadow: '0 0 0 3px rgba(240,180,41,0.15)' }
      : {}),
  });

  return (
    <SignFrame brandNode={<CozeBrand isOversea={IS_OVERSEA} />}>
      <SignPanel
        style={{
          width: 'min(100%, 420px)',
          height: 'auto',
          padding: '40px 28px 32px',
          boxSizing: 'border-box',
          margin: 'auto'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', height: '100%' }}>
          <Favicon />

          {/* Card content — 320 px wide, left-aligned (mirrors OmniChat .auth-card layout) */}
          <div style={{ marginTop: 28, width: '100%', display: 'grid', gap: 10 }}>

            {/* Heading */}
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#162033', letterSpacing: '-0.02em' }}>
                Login
              </h1>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#7a8499' }}>
                Login to access your account
              </p>
            </div>

            {/* Email field */}
            <div style={s.field}>
              <label style={s.label} htmlFor="login-email">Email</label>
              <div style={s.inputWrap}>
                <span style={s.inputIcon}><IconMail /></span>
                <input
                  id="login-email"
                  data-testid="login.input.email"
                  type="email"
                  value={email}
                  required
                  placeholder="Enter your email"
                  style={inputStyle('email')}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>
            </div>

            {/* Password field */}
            <div style={s.field}>
              <label style={s.label} htmlFor="login-password">Password</label>
              <div style={s.inputWrap}>
                <span style={s.inputIcon}><IconLock /></span>
                <input
                  id="login-password"
                  data-testid="login.input.password"
                  type={isPasswordVisible ? 'text' : 'password'}
                  value={password}
                  required
                  placeholder="Enter your password"
                  style={{ ...inputStyle('password'), paddingRight: 38 }}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  onChange={e => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                  onClick={() => setIsPasswordVisible(v => !v)}
                  style={{ position: 'absolute', right: 10, border: 0, background: 'transparent', color: '#9ca3af', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', borderRadius: 6 }}
                >
                  {isPasswordVisible ? <IconEyeOff /> : <IconEye />}
                </button>
              </div>
            </div>

            {/* Remember me */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 500, color: '#374151', cursor: 'pointer' }}>
              <input
                type="checkbox"
                style={{ width: 14, height: 14, accentColor: '#F0B429', cursor: 'pointer' }}
              />
              <span>Remember me</span>
            </label>

            {/* Sign In button */}
            <button
              data-testid="login.button.login"
              type="button"
              disabled={loginDisabled}
              onClick={login}
              style={{
                ...s.submitBtn,
                ...(loginDisabled ? s.submitBtnDisabled : {}),
              }}
            >
              {loginLoading ? 'Signing in…' : 'Sign In'}
            </button>

            {/* Or login with divider */}
            <div style={s.divider}>
              <div style={s.dividerLine} />
              <span style={s.dividerSpan}>Or login with</span>
            </div>

            {/* Continue with Google */}
            <button
              data-testid="login.button.google"
              type="button"
              disabled={loginLoading || googleLoading}
              onClick={loginWithGoogle}
              style={{
                ...s.oauthBtn,
                ...(loginLoading || googleLoading ? { opacity: 0.65, cursor: 'not-allowed' } : {}),
              }}
            >
              <GoogleIcon width={18} height={18} aria-hidden="true" />
              {googleLoading ? 'Redirecting…' : 'Continue with Google'}
            </button>

            {/* Don't have an account? — hidden when self sign-up is closed */}
            {!registrationDisabled && (
              <p style={s.switchCopy}>
                Don&apos;t have an account?{' '}
                <button
                  data-testid="login.link.signup"
                  type="button"
                  style={s.inlineLink}
                  onClick={() => navigate(registerPath)}
                >
                  Sign up
                </button>
              </p>
            )}

          </div>
        </div>
      </SignPanel>
    </SignFrame>
  );
};
